import React, { useState } from 'react';
import {
  QuestionData,
  Option,
  FormTreeData,
} from '@/features/submission_forms/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { X, Trash2, Plus } from 'lucide-react';

interface QuestionEditorProps {
  question: QuestionData;
  allSections: FormTreeData[];
  onUpdate: (updated: Partial<QuestionData>) => void;
  onDelete: () => void;
  onClose: () => void;
  // Shows the settings without letting them change — the Form Builder is
  // read-only while templates are managed in code.
  readOnly?: boolean;
}

export const QuestionEditor: React.FC<QuestionEditorProps> = ({
  question,
  allSections,
  onUpdate,
  onDelete,
  onClose,
  readOnly = false,
}) => {
  const [options, setOptions] = useState<Option[]>(question.options || []);
  const [hasConditionalLogic, setHasConditionalLogic] = useState<boolean>(
    !!question.depends_on
  );

  const questionTypes = [
    { value: 'text', label: 'Text Input' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'select_single', label: 'Single Select' },
    { value: 'select_multi', label: 'Multiple Select' },
    { value: 'date', label: 'Date' },
    { value: 'number', label: 'Number' },
    { value: 'upload', label: 'File Upload' },
  ];

  // Get all available questions from all sections (recursive)
  const getAllQuestions = (
    sections: FormTreeData[]
  ): Array<{ slug: string; prompt: string; kind: string }> => {
    const questions: Array<{ slug: string; prompt: string; kind: string }> = [];

    const traverse = (section: FormTreeData) => {
      section.questions.forEach(q => {
        if (q.id !== question.id) {
          // Don't include current question
          questions.push({
            slug: q.slug,
            prompt: q.prompt,
            kind: q.kind,
          });
        }
      });
      section.children.forEach(child => traverse(child));
    };

    sections.forEach(section => traverse(section));
    return questions;
  };

  const availableQuestions = getAllQuestions(allSections);

  const handleAddOption = () => {
    const newOptions = [
      ...options,
      { label: 'New Option', value: 'New Option' },
    ];
    setOptions(newOptions);
    onUpdate({ options: newOptions });
  };

  const handleUpdateOption = (idx: number, value: string) => {
    const newOptions = options.map((opt, i) =>
      i === idx ? { label: value, value: value } : opt
    );
    setOptions(newOptions);
    onUpdate({ options: newOptions });
  };

  const handleRemoveOption = (idx: number) => {
    const newOptions = options.filter((_, i) => i !== idx);
    setOptions(newOptions);
    onUpdate({ options: newOptions });
  };

  const handleToggleConditionalLogic = (enabled: boolean) => {
    setHasConditionalLogic(enabled);
    if (!enabled) {
      onUpdate({ depends_on: null });
    } else if (!question.depends_on) {
      // Initialize with default values. '=' / '!=' are the only ops
      // SUPPORTED_DEPENDS_ON_OPS (question_behavior.rb) accepts.
      onUpdate({
        depends_on: {
          question_slug: '',
          op: '=',
          value: '',
        },
      });
    }
  };

  const handleUpdateConditionalLogic = (
    field: 'question_slug' | 'op' | 'value',
    value: string
  ) => {
    onUpdate({
      depends_on: {
        question_slug: question.depends_on?.question_slug || '',
        op: question.depends_on?.op || '=',
        value: question.depends_on?.value || '',
        [field]: value,
      },
    });
  };

  const showOptions = ['select_single', 'select_multi'].includes(question.kind);
  // The Answer Assist dials only mean anything on the free-text kinds; every
  // other kind renders a picker whose answers are already canonical.
  const assistDialKinds: QuestionData['kind'][] = ['text', 'textarea'];
  const showAssistDials = assistDialKinds.includes(question.kind);
  // NPI is a physician registry: choosing it *is* declaring the answer to be
  // person data, and the backend's disclosure clamp keys off `person_data`
  // alone. Couple them here so the dial can never be set without the clamp.
  const personDataForced = question.meta?.public_source === 'npi';

  const updateMeta = (patch: Record<string, unknown>) => {
    onUpdate({ meta: { ...(question.meta ?? {}), ...patch } });
  };

  const handleKindChange = (value: string) => {
    const kind = value as QuestionData['kind'];
    if (assistDialKinds.includes(kind) || !question.meta) {
      onUpdate({ kind });
      return;
    }
    // Dials left behind on a non-text kind are invisible in this editor but
    // still snapshot onto submission_questions — drop them with the kind.
    const meta = { ...question.meta };
    delete meta.value_scope;
    delete meta.verify_scope;
    delete meta.public_source;
    onUpdate({ kind, meta });
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold">
          {readOnly ? 'Question' : 'Edit Question'}
        </h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Form */}
      <fieldset disabled={readOnly} className="space-y-4 min-w-0">
        {/* Question Prompt */}
        <div>
          <Label htmlFor="question-prompt">Question Prompt</Label>
          <Textarea
            id="question-prompt"
            // Binds to the raw source (marker intact), not the pre-stripped
            // `prompt` display field — otherwise saving without touching
            // this box would silently overwrite the real prompt with the
            // stripped display copy, destroying any {{...}} marker. Every
            // edit writes both fields so the controlled value follows the
            // keystroke and the tree save (SetTreeService prefers
            // `prompt_template` over `prompt`) persists the marked-up source.
            value={question.prompt_template ?? question.prompt}
            onChange={e =>
              onUpdate({
                prompt: e.target.value,
                prompt_template: e.target.value,
              })
            }
            placeholder="Enter question prompt"
            className="mt-1"
            rows={3}
          />
        </div>

        {/* Question Type */}
        <div>
          <Label htmlFor="question-kind">Question Type</Label>
          <Select value={question.kind} onValueChange={handleKindChange}>
            <SelectTrigger id="question-kind" className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {questionTypes.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Answer Assist family — three per-question dials + person-data flag.
            Copied onto submission_questions at snapshot; features must also be
            enabled tenant-wide (Hub) before any of this changes the live form. */}
        {showAssistDials && (
          <div className="space-y-3 rounded-md border border-teal-100 bg-teal-50/40 p-3">
            <Label className="font-semibold">Answer Assist</Label>

            <div>
              <Label htmlFor="assist-value-scope" className="text-xs">
                Suggested values — who sees this question&apos;s dropdown
              </Label>
              <Select
                value={question.meta?.value_scope ?? 'none'}
                onValueChange={value =>
                  updateMeta({
                    value_scope: value === 'none' ? undefined : value,
                  })
                }
              >
                <SelectTrigger id="assist-value-scope" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    Off — plain text box (default)
                  </SelectItem>
                  <SelectItem value="study_site">
                    This site&apos;s submissions
                  </SelectItem>
                  <SelectItem value="study">
                    Every submission in the study
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="assist-verify-scope" className="text-xs">
                Verify Answer — what the typo-checker compares against
              </Label>
              <Select
                value={question.meta?.verify_scope ?? 'off'}
                onValueChange={value =>
                  updateMeta({
                    verify_scope: value === 'off' ? undefined : value,
                  })
                }
              >
                <SelectTrigger id="assist-verify-scope" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off (default)</SelectItem>
                  <SelectItem value="policy">
                    Only what this viewer could already see
                  </SelectItem>
                  <SelectItem value="study_site">This site</SelectItem>
                  <SelectItem value="study">The study</SelectItem>
                  <SelectItem value="organization">
                    The whole organization
                  </SelectItem>
                  <SelectItem value="authenticated">
                    Univo-curated list
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="assist-public-source" className="text-xs">
                Public Collections — canonical public-record lookup
              </Label>
              <Select
                value={question.meta?.public_source ?? 'off'}
                onValueChange={value =>
                  updateMeta({
                    public_source: value === 'off' ? undefined : value,
                    // Selecting NPI turns person data on and keeps it on.
                    ...(value === 'npi' ? { person_data: true } : {}),
                  })
                }
              >
                <SelectTrigger id="assist-public-source" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off (default)</SelectItem>
                  <SelectItem value="places">
                    Google Places — facilities &amp; addresses
                  </SelectItem>
                  <SelectItem value="npi">NPI Registry — physicians</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-start gap-2">
              <Switch
                id="assist-person-data"
                className="mt-0.5"
                checked={
                  personDataForced || question.meta?.person_data === true
                }
                disabled={personDataForced}
                onCheckedChange={checked =>
                  updateMeta({ person_data: checked ? true : undefined })
                }
              />
              <Label
                htmlFor="assist-person-data"
                className="text-xs font-normal text-gray-700"
              >
                Person data — caps Verify at &quot;what the viewer could already
                see&quot; (a person&apos;s presence in a study is itself
                disclosure)
                {personDataForced && (
                  <span className="mt-1 block font-medium text-teal-700">
                    Always on for the NPI Registry — physician lookups are
                    person data.
                  </span>
                )}
              </Label>
            </div>
          </div>
        )}

        {/* Question Metadata Tags */}
        <div className="space-y-3">
          <Label>Question Settings</Label>
          <div className="flex flex-wrap gap-2">
            {/* Required Tag */}
            <button
              type="button"
              onClick={() => onUpdate({ required: !question.required })}
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                question.required
                  ? 'bg-red-100 text-red-800 hover:bg-red-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {question.required ? '✓ Required' : 'Required'}
            </button>

            {/* Show on Details Tag */}
            <button
              type="button"
              onClick={() => onUpdate({ details: !question.details })}
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                question.details
                  ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {question.details ? '✓ Show on Details' : 'Show on Details'}
            </button>

            {/* Conditional Logic Tag */}
            {hasConditionalLogic && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                ✓ Conditional
              </span>
            )}
          </div>
        </div>

        {/* Details Label (conditionally shown) */}
        {question.details && (
          <div>
            <Label htmlFor="question-details-label">Details Label</Label>
            <Input
              id="question-details-label"
              value={question.details_label || ''}
              onChange={e => onUpdate({ details_label: e.target.value })}
              placeholder="Leave empty to use question prompt"
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              If left empty, the question prompt will be used as the details
              label.
            </p>
          </div>
        )}

        {/* Options (for select types) */}
        {showOptions && (
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <Label>Options</Label>
              <Button variant="outline" size="sm" onClick={handleAddOption}>
                <Plus className="w-3 h-3 mr-1" />
                Add Option
              </Button>
            </div>
            <div className="space-y-2">
              {options.map((option, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input
                    placeholder="Option"
                    value={option.label}
                    onChange={e => handleUpdateOption(idx, e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveOption(idx)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Conditional Logic */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-3">
            <Label htmlFor="conditional-logic-toggle">Conditional Logic</Label>
            <Switch
              id="conditional-logic-toggle"
              checked={hasConditionalLogic}
              onCheckedChange={handleToggleConditionalLogic}
            />
          </div>

          {hasConditionalLogic && (
            <div className="space-y-3 bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-3">
                Show this question only when the selected question does or does
                not equal the specified value:
              </p>

              {/* Depends On Question */}
              <div>
                <Label htmlFor="depends-on-question" className="text-sm">
                  Question
                </Label>
                <Select
                  value={question.depends_on?.question_slug || ''}
                  onValueChange={value =>
                    handleUpdateConditionalLogic('question_slug', value)
                  }
                >
                  <SelectTrigger className="mt-1 bg-white">
                    <SelectValue placeholder="Select a question..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {availableQuestions.length === 0 ? (
                      <div className="px-2 py-1 text-sm text-gray-500">
                        No other questions available
                      </div>
                    ) : (
                      availableQuestions.map(q => (
                        <SelectItem key={q.slug} value={q.slug}>
                          {q.prompt || q.slug}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Condition */}
              <div>
                <Label htmlFor="depends-on-op" className="text-sm">
                  Condition
                </Label>
                <Select
                  value={question.depends_on?.op || '='}
                  onValueChange={value =>
                    handleUpdateConditionalLogic('op', value)
                  }
                >
                  <SelectTrigger id="depends-on-op" className="mt-1 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="=">Equals</SelectItem>
                    <SelectItem value="!=">Does not equal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Value */}
              <div>
                <Label htmlFor="depends-on-value" className="text-sm">
                  Value
                </Label>
                <Input
                  id="depends-on-value"
                  value={question.depends_on?.value || ''}
                  onChange={e =>
                    handleUpdateConditionalLogic('value', e.target.value)
                  }
                  placeholder="Enter value..."
                  className="mt-1 bg-white"
                />
              </div>
            </div>
          )}
        </div>

        {/* Danger Zone */}
        {!readOnly && (
          <div className="border-t pt-4 mt-4">
            <Button
              variant="destructive"
              size="sm"
              className="w-full"
              onClick={() => {
                if (
                  confirm(
                    'Are you sure you want to delete this question? This action cannot be undone.'
                  )
                ) {
                  onDelete();
                  onClose();
                }
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Question
            </Button>
          </div>
        )}
      </fieldset>
    </div>
  );
};
